/**
 * Traceur de courbes — lecture de la formule tapée par l'élève. PUR.
 *
 * L'élève écrit comme sur sa copie : « 2x + 1 », « ax² + bx + c »,
 * « a(x − 2)² », « √x », « 3sin(x) ». On traduit vers la syntaxe de
 * l'évaluateur partagé (`@jeraisonne/db/shared/expr` : multiplication
 * explicite, `^`, `sqrt(...)`) puis on vérifie la formule en l'évaluant.
 *
 * Lettres permises : x (la variable), a, b, c, k, m, p (paramètres réglés par
 * des curseurs), e (constante), π. Les fonctions s'écrivent avec des
 * parenthèses : sin(x), cos(x), tan(x), √(x) ou √x, ln(x), exp(x), abs(x).
 */

import { evalExpr } from '@jeraisonne/db/shared/expr';
import { formatNumber } from '../../ui/format';

export const PARAM_NAMES = ['a', 'b', 'c', 'k', 'm', 'p'] as const;
export type ParamName = (typeof PARAM_NAMES)[number];

const FUNCTIONS = ['sqrt', 'sin', 'cos', 'tan', 'abs', 'exp', 'ln', 'log'] as const;
const LETTERS = new Set<string>(['x', 'e', ...PARAM_NAMES]);

type Tok =
  | { t: 'num'; v: string }
  | { t: 'var'; v: string }
  | { t: 'fn'; v: string }
  | { t: 'op'; v: string }
  | { t: '(' }
  | { t: ')' };

export type Compiled =
  | { ok: true; expr: string; params: ParamName[] }
  | { ok: false; error: string };

/** symboles de la copie → ASCII ; « 3,5 » → « 3.5 » */
function ascii(src: string): string {
  return src
    .replace(/[×·∙]/g, '*')
    .replace(/÷/g, '/')
    .replace(/[−–]/g, '-')
    .replace(/²/g, '^2')
    .replace(/³/g, '^3')
    .replace(/π/g, ' pi ')
    .replace(/(\d),(\d)/g, '$1.$2')
    .replace(/\*\*/g, '^');
}

function tokenize(src: string): Tok[] | string {
  const s = ascii(src);
  const out: Tok[] = [];
  let i = 0;
  while (i < s.length) {
    const ch = s[i]!;
    if (/\s/.test(ch)) {
      i++;
      continue;
    }
    if (/[0-9.]/.test(ch)) {
      let j = i;
      while (j < s.length && /[0-9.]/.test(s[j]!)) j++;
      const v = s.slice(i, j);
      if ((v.match(/\./g) ?? []).length > 1) return `Nombre mal écrit : « ${v} »`;
      out.push({ t: 'num', v });
      i = j;
      continue;
    }
    if (ch === '√') {
      out.push({ t: 'fn', v: 'sqrt' });
      i++;
      continue;
    }
    if (/[a-zA-Z]/.test(ch)) {
      let j = i;
      while (j < s.length && /[a-zA-Z]/.test(s[j]!)) j++;
      const word = s.slice(i, j).toLowerCase();
      // un mot = une suite de fonctions, de « pi » et de lettres seules
      let w = 0;
      while (w < word.length) {
        const name = ['pi', ...FUNCTIONS].find((n) => word.startsWith(n, w));
        if (name === 'pi') {
          out.push({ t: 'var', v: 'pi' });
          w += 2;
        } else if (name) {
          out.push({ t: 'fn', v: name });
          w += name.length;
        } else {
          const l = word[w]!;
          if (!LETTERS.has(l))
            return `Lettre inconnue : « ${l} ». Utilise x, et a, b, c, k, m ou p pour les curseurs.`;
          out.push({ t: 'var', v: l });
          w++;
        }
      }
      i = j;
      continue;
    }
    if ('+-*/^'.includes(ch)) {
      out.push({ t: 'op', v: ch });
      i++;
      continue;
    }
    if (ch === '(' || ch === '[') {
      out.push({ t: '(' });
      i++;
      continue;
    }
    if (ch === ')' || ch === ']') {
      out.push({ t: ')' });
      i++;
      continue;
    }
    return `Caractère inconnu : « ${ch} »`;
  }
  return out;
}

const endsValue = (t: Tok | undefined) => !!t && (t.t === 'num' || t.t === 'var' || t.t === ')');
const startsValue = (t: Tok) => t.t === 'num' || t.t === 'var' || t.t === 'fn' || t.t === '(';

/**
 * Formule de l'élève → expression pour l'évaluateur, avec ses paramètres.
 * « √x » devient « sqrt(x) » ; une fonction sans parenthèse est refusée.
 */
export function compileFormula(src: string): Compiled {
  if (!src.trim()) return { ok: false, error: 'Écris une formule, par exemple 2x + 1.' };
  const toks = tokenize(src);
  if (typeof toks === 'string') return { ok: false, error: toks };

  const parts: string[] = [];
  const params = new Set<ParamName>();
  for (let i = 0; i < toks.length; i++) {
    const tok = toks[i]!;
    if (endsValue(toks[i - 1]) && startsValue(tok)) parts.push('*');
    if (tok.t === 'fn') {
      const next = toks[i + 1];
      if (tok.v === 'sqrt' && next && (next.t === 'num' || next.t === 'var')) {
        // √x, √2 : la racine porte sur le seul nombre ou la seule lettre qui suit
        parts.push(`sqrt(${next.v})`);
        if (next.t === 'var' && (PARAM_NAMES as readonly string[]).includes(next.v)) {
          params.add(next.v as ParamName);
        }
        i++;
        // la racine se comporte comme une valeur : « √x x » → « sqrt(x)*x »
        toks[i] = { t: ')' };
        continue;
      }
      if (!next || next.t !== '(') {
        const shown = tok.v === 'sqrt' ? '√' : tok.v;
        return { ok: false, error: `Mets des parenthèses : ${shown}(x).` };
      }
      parts.push(tok.v);
      continue;
    }
    if (tok.t === 'var') {
      if ((PARAM_NAMES as readonly string[]).includes(tok.v)) params.add(tok.v as ParamName);
      parts.push(tok.v);
      continue;
    }
    if (tok.t === 'num' || tok.t === 'op') parts.push(tok.v);
    else parts.push(tok.t);
  }
  const expr = parts.join('');

  const scope: Record<string, number> = { x: 0.7 };
  for (const name of PARAM_NAMES) scope[name] = 1.3;
  try {
    evalExpr(expr, scope);
  } catch {
    return { ok: false, error: 'Formule incomplète : vérifie les parenthèses et les opérations.' };
  }
  return { ok: true, expr, params: PARAM_NAMES.filter((n) => params.has(n)) };
}

/** f(x) pour des valeurs de paramètres ; NaN hors du domaine. */
export function evaluate(
  expr: string,
  x: number,
  values: Partial<Record<ParamName, number>>,
): number {
  try {
    const scope: Record<string, number> = { x };
    for (const n of PARAM_NAMES) scope[n] = values[n] ?? 1;
    const y = evalExpr(expr, scope);
    return Number.isFinite(y) ? y : NaN;
  } catch {
    return NaN;
  }
}

/** Valeur par défaut d'un curseur : a, k, m = 1 ; b, c, p = 0. */
export function defaultParam(name: ParamName): number {
  return name === 'a' || name === 'k' || name === 'm' ? 1 : 0;
}

export interface Preset {
  label: string;
  formula: string;
  /** rang minimal (3e = 4, 2nde = 5, 1re = 6, Tle = 7) */
  minRank: number;
}

export const PRESETS: readonly Preset[] = [
  { label: 'Affine', formula: 'ax + b', minRank: 4 },
  { label: 'Carré', formula: 'a(x − b)² + c', minRank: 5 },
  { label: 'Trinôme', formula: 'ax² + bx + c', minRank: 6 },
  { label: 'Inverse', formula: 'a/x', minRank: 5 },
  { label: 'Racine', formula: 'a√(x − b)', minRank: 5 },
  { label: 'Cube', formula: 'ax³', minRank: 5 },
  { label: 'Exponentielle', formula: 'exp(kx)', minRank: 6 },
  { label: 'Sinus', formula: 'a sin(kx)', minRank: 6 },
  { label: 'Logarithme', formula: 'ln(x)', minRank: 7 },
];

/** « 2,25 », « −0,333 » */
export { formatNumber };

/**
 * Ce qu'un modèle connu permet de lire avec les curseurs : pente, sommet,
 * discriminant et racines. null pour une formule libre.
 */
export function presetNote(
  formula: string,
  values: Partial<Record<ParamName, number>>,
): string | null {
  const f = ascii(formula).replace(/\s+/g, '');
  const a = values.a ?? 1;
  const b = values.b ?? 0;
  const c = values.c ?? 0;
  const n = (v: number) => formatNumber(v);
  if (f === 'ax+b') {
    return `Pente ${n(a)}, ordonnée à l’origine ${n(b)}. ${
      a > 0 ? 'La droite monte.' : a < 0 ? 'La droite descend.' : 'La droite est horizontale.'
    }`;
  }
  if (f === 'a(x-b)^2+c') {
    if (a === 0) return 'a = 0 : ce n’est plus une parabole, mais la droite y = c.';
    return `Sommet (${n(b)} ; ${n(c)}), parabole tournée vers le ${a > 0 ? 'haut' : 'bas'}.`;
  }
  if (f === 'ax^2+bx+c') {
    if (a === 0) return 'a = 0 : ce n’est plus un trinôme du second degré.';
    const delta = b * b - 4 * a * c;
    const d = `Δ = b² − 4ac = ${n(delta)}`;
    if (Math.abs(delta) < 1e-9) return `${d} : une racine double, x₀ = ${n(-b / (2 * a))}.`;
    if (delta < 0) return `${d} < 0 : aucune racine, la parabole ne coupe pas l’axe.`;
    const r1 = (-b - Math.sqrt(delta)) / (2 * a);
    const r2 = (-b + Math.sqrt(delta)) / (2 * a);
    const [lo, hi] = r1 < r2 ? [r1, r2] : [r2, r1];
    return `${d} > 0 : deux racines, x₁ ≈ ${n(lo)} et x₂ ≈ ${n(hi)}.`;
  }
  return null;
}
